import { Breadcrumb, IBreadcrumbItem, IContextualMenuItem, Label, MessageBar, MessageBarType, Panel, PanelType, Pivot, PivotItem, Separator, Stack } from '@fluentui/react';
import { Market, Patient as PatientData, PatientPreferredDoctor, visitSearchByPatientId } from '../../api';
import { InsuranceCard } from '../../components/core/InsuranceCard';
import * as React from 'react';
import { PATIENT_TYPE } from '../../config/constants';
import { ROUTES } from '../../config/routes';
import { HealAPI } from '../../healApi';
import { AsyncData } from '../../redux/core';
import { ToastParams } from '../../redux/message/types';
import { CreatePatientRequest, EditPatientRequest } from '../../redux/patient/types';
import { Properties, Visit } from '../../redux/visits/types';
import { StatusOptions } from '../../types';
import { AsyncVisitSidePanel } from '../core/AsyncVisitSidePanel';
import { PageHeader } from '../core/PageHeader';
import { PageSidePanel } from '../core/PageSidePanel';
import { VisitList } from '../core/VisitList';
import { DeceasedPatientDialog } from './dialogs/DeceasedPatient';
import { PatientInsurancesTable } from './insurances/PatientInsurancesTable';
import { PatientFiles } from './PatientFiles';
import { PatientForm } from './PatientForm';
import { PatientProfileTab } from './profile/PatientProfileTab';
import { NavigateFunction } from 'react-router-dom';
import CareTeam from '../pod/CareTeam';
import { Address } from '../../api/accounts.types';
import './Patient.scss';

export interface PatientOwnProps {
  navigate: NavigateFunction;
  params: { id: string; tab: string };
}
export interface PatientStateProps {
  visitConfig: AsyncData<Properties>;
  markets: AsyncData<Market[]>;
  visitCode: string;
}
export interface PatientDispatchProps {
  actions: {
    onPageDidMount: (name: string) => void;
    onPageWillUnmount: (name: string) => void;
    setVisitCode: (visitCode: string) => void;
    toast: (params: ToastParams) => void;
  };
}

export type PatientProps = PatientOwnProps & PatientStateProps & PatientDispatchProps;

export interface PatientState {
  model: AsyncData<PatientData>;
  visits: AsyncData<Visit[]>;
  showEditPatientPanel: boolean;
  isSaving?: boolean;
  preferredDoctor: AsyncData<PatientPreferredDoctor>;
  userAccountName?: string;
  accountStatus?: string;
  homeAddress?: Address;
}

export class Patient extends React.Component<PatientProps, PatientState> {
  constructor(props: PatientProps) {
    super(props);
    this.state = {
      model: { isFetching: false },
      visits: { isFetching: false },
      showEditPatientPanel: false,
      preferredDoctor: { isFetching: false },
    };
  }

  public componentDidMount() {
    this.props.actions.onPageDidMount('Patient');
    this.fetchPatient(this.props.params.id, true);
  }

  public componentWillUnmount() {
    this.props.actions.onPageWillUnmount('Patient');
  }

  public render(): JSX.Element {
    if (this.state.model.error) {
      return (
        <div style={{ marginTop: 20, maxWidth: 600 }}>
          <MessageBar messageBarType={MessageBarType.error}>{this.state.model.error}</MessageBar>
        </div>
      );
    }

    const breadCrumbItems: IBreadcrumbItem[] = [{ text: 'Users', key: 'users', onClick: this.onUsersClick }];

    if (this.state.model.data && this.state.model.data.ownerAccountId) {
      breadCrumbItems.push({
        text: this.state.userAccountName || this.state.model.data.ownerAccountId,
        key: 'account',
        onClick: this.onAccountClick,
      });
    }

    breadCrumbItems.push({ text: this.getName(), key: 'patient', isCurrentItem: true });

    const breadcrumb = (
      <Breadcrumb
        items={breadCrumbItems}
        // Returning undefined to OnReduceData tells the breadcrumb not to shrink
        onReduceData={() => undefined}
      />
    );

    return (
      <div className="patient-page">
        <PageHeader
          documentTitle={this.state.model.data?.lastName || 'Loading...'}
          title={breadcrumb}
          isLoading={this.state.model.isFetching}
          actions={{ items: this.getActions(), type: 'dropdown' }}
          showShareButton
        />
        {this.state.accountStatus === 'STATUS_DISABLED' && <MessageBar messageBarType={MessageBarType.severeWarning}>The top level user account is disabled.</MessageBar>}
        {this.state.model.data?.status === 'DELETED_BY_ACCOUNT' && <MessageBar messageBarType={MessageBarType.severeWarning}>This patient is deleted in the account.</MessageBar>}

        <Pivot onLinkClick={this.onPivotLinkClick} selectedKey={this.props.params.tab || 'profile'}>
          <PivotItem headerText="Profile" itemKey="profile" data-tid="btn_navtab_profile">
            <PatientProfileTab
              patient={this.state.model.data}
              onPatientUpdated={this.onPatientUpdated}
              preferredDoctorState={this.state.preferredDoctor}
              onSecondaryContactUpdated={(patient) => this.setState({ model: { isFetching: false, data: patient } })}
              homeAddress={this.state.homeAddress}
            />
          </PivotItem>
          <PivotItem headerText="Visits" itemKey="visits" data-tid="btn_navtab_visits">
            {this.renderVisits()}
            {this.renderVisitSidePanel()}
          </PivotItem>
          <PivotItem headerText="Files" itemKey="files" data-tid="btn_navtab_files">
            <PatientFiles visits={this.state.visits} patientId={this.props.params.id} patient={this.state.model.data} />
          </PivotItem>
          <PivotItem headerText="Insurances" itemKey="insurances" data-tid="btn_navtab_insurances">
            <Stack root={{ style: { marginTop: 10 } }} tokens={{ childrenGap: 12 }}>
              <PatientInsurancesTable patientId={this.props.params.id} />
              <Separator />
              <Label>Insurance Card Images</Label>
              <MessageBar messageBarType={MessageBarType.info} role="none" styles={{ root: { maxWidth: 600 } }} isMultiline>
                <strong>Recently saved insurance card images. May not to insurances in the table above.</strong>
              </MessageBar>
              <InsuranceCard patientId={this.props.params.id} />
            </Stack>
          </PivotItem>
          <PivotItem headerText="Care Team" itemKey="careteam" data-tid="btn_navtab_careteam">
            <CareTeam patient={this.state.model.data} />
          </PivotItem>
        </Pivot>
        {this.renderPatientSidePanel()}
      </div>
    );
  }

  private onPivotLinkClick = (item?: PivotItem) => {
    this.props.actions.setVisitCode('');
    this.props.navigate(`${ROUTES.patients}/${this.props.params.id}/${item!.props.itemKey!}`);
  };

  private getActions(): IContextualMenuItem[] {
    if (this.state.model.isFetching || !this.state.model.data) {
      return [];
    }

    const patient = this.state.model.data;
    const items: IContextualMenuItem[] = [];

    items.push({
      key: 'Edit Profile',
      name: 'Edit Profile',
      iconProps: { iconName: 'Edit' },
      onClick: () => this.setState({ showEditPatientPanel: true }),
    });

    if (patient) {
      items.push({
        key: 'Create Visit',
        name: 'Create Visit',
        iconProps: { iconName: 'AddFriend' },
        onClick: () => this.props.navigate(ROUTES.createVisit + '?uid=' + patient.ownerAccountId + '&pid=' + patient.patientId),
      });

      items.push({
        key: 'Check Eligibility',
        name: 'Check Eligibility',
        iconProps: { iconName: 'ContactCard' },
        onClick: () => this.props.navigate(ROUTES.toolInsurance + '?pid=' + patient.patientId),
      });

      items.push({
        key: 'Copy Patient Key',
        name: 'Copy Patient Key',
        iconProps: { iconName: 'AuthenticatorApp' },
        onClick: this.copyPatientKey,
      });
    }

    if (!patient.deceasedDate) {
      items.push({
        key: 'Mark Patient as Deceased',
        name: 'Mark Patient as Deceased',
        iconProps: { iconName: 'ProtectedDocument' },
        onClick: () => DeceasedPatientDialog.show({ patientId: patient.patientId, onComplete: () => this.fetchPatient(patient.patientId), toast: this.props.actions.toast }),
      });
    }

    return items;
  }

  private copyPatientKey = () => {
    const key = btoa(`{"userAccountId": "${this.state.model.data!.ownerAccountId}","patientId": "${this.props.params.id}"} `);
    navigator.clipboard.writeText(key);
    this.props.actions.toast({
      type: 'info',
      title: 'Copy to clipboard',
      content: 'Patient key has been copied to your clipboard',
    });
  };

  private renderVisitSidePanel(): JSX.Element | null {
    if (!this.props.visitCode) {
      return null;
    }
    return (
      <PageSidePanel>
        <AsyncVisitSidePanel visitCode={this.props.visitCode} />
      </PageSidePanel>
    );
  }

  private renderPatientSidePanel(): JSX.Element | null {
    const patient = this.state.model.data;
    if (!patient) {
      return null;
    }

    return (
      <Panel isOpen={this.state.showEditPatientPanel} onDismiss={() => this.setState({ showEditPatientPanel: false })} type={PanelType.medium} headerText={'Edit patient'}>
        <PatientForm patient={patient} isLoading={this.state.isSaving} payers={this.props.visitConfig.data ? this.props.visitConfig.data.payers : []} onSave={this.onSave} />
      </Panel>
    );
  }

  private onSave = (patientId: string, patient: CreatePatientRequest | EditPatientRequest) => {
    this.setState({ isSaving: true });
    HealAPI.editPatient(patientId, patient).then(
      (response) => {
        if (response.ok && response.data) {
          this.props.actions.toast({
            type: 'success',
            title: 'Success',
            content: 'Patient information has been updated',
          });
          this.fetchPatient(patientId);
          this.setState({ showEditPatientPanel: false });
        } else {
          this.props.actions.toast({
            type: 'error',
            title: 'Error',
            content: (response.data || ({} as any)).description || 'Patient could not be updated',
          });
        }
        this.setState({ isSaving: false });
      },
      (response) => {
        this.props.actions.toast({
          type: 'error',
          title: 'Error',
          content: (response.data || ({} as any)).description || 'Patient could not be updated',
        });
        this.setState({ isSaving: false });
      }
    );
  };

  private onPatientUpdated = (patientId: string) => {
    this.fetchPatient(patientId);
  };

  private renderVisits(): JSX.Element {
    if (this.state.visits.error) {
      return (
        <div style={{ marginTop: 20, maxWidth: 600 }}>
          <MessageBar messageBarType={MessageBarType.error}>{this.state.visits.error}</MessageBar>
        </div>
      );
    }

    const columns = ['insurance', 'code', 'status', 'service', 'date', 'duration', 'doctor', 'address'];

    return (
      <>
        <VisitList
          isFetching={this.state.visits.isFetching}
          visits={this.state.visits.data || []}
          columns={columns}
          markets={this.props.markets}
          onSelected={this.onVisitSelected}
          selectedVisitCode={this.props.visitCode}
        />
        {!!this.state.model.data && !this.state.model.isFetching && this.state.visits.data && this.state.visits.data.length === 0 && (
          <MessageBar messageBarType={MessageBarType.info}>No visits for this patient exist.</MessageBar>
        )}
      </>
    );
  }

  private getName(): string {
    const patient = this.state.model.data;
    if (!patient) {
      return 'Loading...';
    }

    const relationship = PATIENT_TYPE.find((item: any) => item.key === patient.relationshipId);
    let suffix = '';
    if (relationship) {
      suffix = ' (' + (relationship.text === 'You' ? 'Self' : relationship.text) + ')';
    }
    return `${patient.firstName} ${patient.lastName}${suffix}`;
  }

  private onUsersClick = () => {
    this.props.navigate(ROUTES.patients);
  };

  private onAccountClick = () => {
    this.props.navigate(`${ROUTES.accounts}/${this.state.model.data!.ownerAccountId}`);
  };

  private onVisitSelected = (visitCode: string) => {
    this.props.actions.setVisitCode(visitCode);
  };

  private fetchPatient(patientId: string, fetchVisit?: boolean) {
    this.setState({ model: { isFetching: true } });
    HealAPI.fetchPatient(patientId).then((response) => {
      if (response && response.data && response.data.status === StatusOptions.OK) {
        const patientModel: PatientData = response.data.patient || response.data.data;

        this.fetchAccountName(patientModel.ownerAccountId);
        this.fetchPreferredDoctor(patientId);

        if (fetchVisit) {
          this.fetchPatientVisits(patientId);
        }

        this.setState({ model: { isFetching: false, data: patientModel } });
      } else {
        this.setState({ model: { isFetching: false, error: 'The patient does not exist.' } });
      }
    });
  }

  private fetchAccountName(accountId: string) {
    this.setState({ userAccountName: 'Loading...' });
    HealAPI.fetchAccountInfo(accountId).then((response) => {
      if (response && response.data) {
        var homeAddress = response.data.addresses.find((a) => a.addressType === 'HOME');
        this.setState({ userAccountName: response.data.fullName, accountStatus: response.data.status, homeAddress: homeAddress });
      } else {
        this.setState({ userAccountName: undefined, accountStatus: undefined, homeAddress: undefined });
      }
    });
  }

  private fetchPatientVisits(patientId: string) {
    this.setState({ visits: { isFetching: true } });
    visitSearchByPatientId(patientId).then(
      (result) => {
        const data: any[] = result.filter((v) => v.patientId === patientId);
        this.setState({ visits: { isFetching: false, data } });
      },
      () => {
        this.setState({ visits: { isFetching: false, error: 'Failed to retrieve visits for this patient.' } });
      }
    );
  }

  private fetchPreferredDoctor(patientId: string) {
    this.setState({ preferredDoctor: { isFetching: true } });
    HealAPI.fetchPatientPreferredDoctor(patientId).then((response: any) => {
      if (response.ok) {
        this.setState({ preferredDoctor: { data: response.data, isFetching: false } });
      } else {
        this.setState({
          preferredDoctor: {
            isFetching: false,
            error: "An error occurred while fetching this Patient's Preferred Doctor, please try to refresh the page.",
          },
        });
      }
    });
  }
}
